import { useCallback, useEffect, useRef } from 'react';
import { Outlet, useNavigationType, useParams } from 'react-router-dom';

import { Divider, Flex, VFlex } from '@/components/base';
import AMP_KEYS from '@/configs/amp-track-keys';
import { WORKSPACE_IDS } from '@/configs/workspace-ids';
import AskSynclyFloat from '@/features/AskSyncly/components/AskSynclyFloat/AskSynclyFloat';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import useInsightExplorerFeedbackListModal from '@/features/InsightExplorer/hooks/useInsightExplorerFeedbackListModal';
import { insightExplorerUtils } from '@/features/InsightExplorer/utils/insight-explorer.utils';
import useWorkspaceId from '@/hooks/domain/useWorkspaceId';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { useResizeHandle } from '@/hooks/useResizeHandle';
import useInsightExplorerStore from '@/stores/InsightExplorerStore';
import useTagStore from '@/stores/TagStore';
import { track } from '@/utils/track/track';

import useExplorerPaylaod from '../../hooks/useExplorerPaylaod';
import useExplorerSelect from '../../hooks/useExplorerSelect';
import { fetchTagTree, fetchTaxonomyTree } from '../../hooks/useFieldTree';
import {
  InsightExplorerFieldType,
  InsightExplorerFieldTypeMap,
} from '../../types/constants';
import { InsightExplorerHeader } from '../InsightExplorerHeader';
import { InsightExplorerSidebar } from '../InsightExplorerSidebar';
import { ResizeHandle, Style } from './styles';

const InsightExplorerLayout = () => {
  const isLoadingRef = useRef(false);
  const treeInitializedRef = useRef(false);
  const wr = useWorkspaceRouter();
  const workspaceId = useWorkspaceId();
  const { field_type, node_id } = useParams();
  const navigationType = useNavigationType();
  const tagMap = useTagStore((state) => state.tagMap);
  const selectNode = useExplorerSelect();
  const { selectedNode, view, treeData, setState, findNode } =
    useInsightExplorerStore();

  const { filterPayload } = useExplorerPaylaod();

  const openInsightExplorerFeedbackListModal =
    useInsightExplorerFeedbackListModal();

  const { elementRef, handleMouseDown, isResizing } = useResizeHandle({
    initialWidth: 320,
    minWidth: 280,
    maxWidth: 480,
  });

  const onAskSyncly = useCallback(
    (prompt: string) => {
      if (!selectedNode) return;
      track(AMP_KEYS.INSIGHT_EXPLORER_ASK_SYNCLY_SUBMIT, {
        prompt,
      });
      const filter = insightExplorerUtils
        .getFilterByExplorerOptions({
          view,
          filterPayload,
          selectedNode,
        })
        .get();
      openInsightExplorerFeedbackListModal({
        filter,
        socketInfo: {
          initialPrompt: prompt,
          initialState: CollapseState.EXPANDED,
        },
      });
    },
    [selectedNode, view, filterPayload, openInsightExplorerFeedbackListModal],
  );

  const defaultField: InsightExplorerFieldType =
    workspaceId === WORKSPACE_IDS.HITE_JINRO && !field_type
      ? InsightExplorerFieldType.TAG
      : InsightExplorerFieldType.TAXONOMY;
  const selectedField = (field_type as InsightExplorerFieldType) || defaultField;
  const title = InsightExplorerFieldTypeMap.get(selectedField);

  const fetchTreeData = useCallback(
    async (field: InsightExplorerFieldType) => {
      if (field === InsightExplorerFieldType.TAG) {
        return fetchTagTree(tagMap);
      }
      return await fetchTaxonomyTree();
    },
    [tagMap],
  );

  const initTree = useCallback(
    async (field_type: InsightExplorerFieldType, nodeId?: string) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      const tree = await fetchTreeData(field_type);
      setState({
        treeData: tree,
        by: insightExplorerUtils.getByFromFieldType(field_type),
      });
      let node = tree.data[0];
      if (nodeId) {
        node = tree.map[nodeId];
      }
      if (!node) throw new Error('node is not found');
      setState({
        selectedNode: node,
      });
      const field = insightExplorerUtils.getFieldFromNodeType(node.node_type);
      wr.push(`/insight-explorer/${field}/${node.id}`, {
        replace: true,
      });
      isLoadingRef.current = false;
      treeInitializedRef.current = true;
    },
    [],
  );

  useEffect(() => {
    if (treeInitializedRef.current) {
      const currentField = selectedField;
      if (
        selectedNode &&
        insightExplorerUtils.getFieldFromNodeType(selectedNode.node_type) ===
          currentField
      )
        return;
      if (selectedNode?.id === node_id) return;
    }

    initTree(selectedField, node_id);
  }, [selectedField, node_id, selectedNode, initTree]);

  useEffect(() => {
    if (navigationType !== 'POP') return;
    if (!node_id || !field_type) return;
    if (selectedNode?.id === node_id) return;
    const node = findNode(node_id);
    if (!node) return;
    setState({
      selectedNode: node,
    });
  }, [
    field_type,
    node_id,
    selectedNode?.id,
    navigationType,
    findNode,
    setState,
  ]);

  return (
    <VFlex height="100%">
      <InsightExplorerHeader />
      <Divider />
      <Style>
        <VFlex ref={elementRef} className="sidebar-area">
          <InsightExplorerSidebar
            title={title}
            selectedField={selectedField}
            treeData={treeData}
            onNodeSelect={(node) => {
              selectNode(node);
            }}
            onFieldChange={(field) => {
              wr.push(`/insight-explorer/${field}`);
            }}
            selectedNodeId={selectedNode?.id}
          />
          <ResizeHandle
            onMouseDown={handleMouseDown}
            className={isResizing ? 'resizing' : ''}
          />
        </VFlex>
        <Flex f={1} className="content-area">
          <Outlet />
          <AskSynclyFloat
            onAskSyncly={onAskSyncly}
            placeholder={
              selectedNode
                ? `Ask anything about '${selectedNode?.name}'`
                : 'Ask anything'
            }
          />
        </Flex>
      </Style>
    </VFlex>
  );
};

export default InsightExplorerLayout;
